import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// Always use relative /api path - proxy routes it on all domains
const API_PREFIX = '/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async (attempt = 1) => {
      try {
        setStatus('processing');
        
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          setStatus('error');
          setError('No session ID found. Please try logging in again.');
          setTimeout(() => navigate('/', { replace: true }), 2000);
          return;
        }

        // Create session with backend - use relative URL (same-origin, no CORS)
        const response = await axios.post(
          `${API_PREFIX}/auth/session`,
          null,
          { 
            params: { session_id: sessionId },
            withCredentials: true,
            timeout: 30000,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        const user = response.data;
        setStatus('success');
        
        // Store user session data in localStorage for persistence
        localStorage.setItem('vgws_user', JSON.stringify({
          ...user,
          session_created: Date.now(),
          session_expires: Date.now() + (365 * 24 * 60 * 60 * 1000)
        }));
        localStorage.removeItem('login_initiated');
        
        // Delay for mobile browsers to set cookie properly
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Honor intended role selected on Landing, but only if RBAC allows it
        let intendedTarget = null;
        try {
          intendedTarget = localStorage.getItem('vgws_intended_target');
        } catch (err) {
          // localStorage unavailable — fall back to RBAC default
          if (process.env.NODE_ENV !== 'production') {
            console.debug('AuthCallback: localStorage.getItem failed', err?.message);
          }
          intendedTarget = null;
        }

        const allowedTargets = {
          cao: ['/cao', '/dashboard'],
          process_owner: ['/process-owner', '/dashboard'],
          parent_staff: ['/dashboard'],
          student: ['/dashboard'],
        };
        const defaultTarget =
          user.role === 'cao' ? '/cao' :
          user.role === 'process_owner' ? '/process-owner' :
          '/dashboard';
        const allowed = allowedTargets[user.role] || ['/dashboard'];
        const finalTarget = intendedTarget && allowed.includes(intendedTarget) ? intendedTarget : defaultTarget;

        try {
          // Keep 'vgws_intended_role' so CreateTicketDialog knows which form
          // variant (Parent / Student / Staff) to render. It is only
          // overwritten when the user clicks a different role on Landing.
          localStorage.removeItem('vgws_intended_target');
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('AuthCallback: localStorage.removeItem failed', err?.message);
          }
        }

        navigate(finalTarget, { replace: true, state: { user } });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Auth error:', error?.message, error?.code, error?.response?.status);
        }
        
        // Retry on network errors (up to 3 retries with exponential backoff)
        const isNetworkError = !error.response && (
          error.code === 'ECONNABORTED' || 
          error.code === 'ERR_NETWORK' || 
          error.message?.includes('Network Error') ||
          error.message?.includes('timeout')
        );
        
        if (attempt < 4 && isNetworkError) {
          setRetryCount(attempt);
          setStatus('retrying');
          const delay = Math.min(2000 * attempt, 6000);
          await new Promise(resolve => setTimeout(resolve, delay));
          hasProcessed.current = false;
          return processSession(attempt + 1);
        }
        
        setStatus('error');

        // Surface the most useful diagnostic available so a real "Authentication
        // failed" never leaves the user (or the dev team) guessing. Priority
        // order: server-supplied detail → HTTP status hint → network/timeout
        // hint → generic fallback.
        const serverDetail = error.response?.data?.detail;
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          setError('Connection timeout. The server may be busy. Please try again.');
        } else if (error.response?.status === 400) {
          setError(serverDetail || 'Session expired or invalid. Please try logging in again.');
        } else if (error.response?.status === 401) {
          setError(serverDetail || 'Your sign-in is no longer valid. Please log in again.');
        } else if (error.response?.status === 403) {
          setError(serverDetail || 'Access denied for this account. Contact support@mitgurukul.com.');
        } else if (error.response?.status === 404) {
          setError(serverDetail || 'Account not registered. Please contact your admin.');
        } else if (error.response?.status === 504) {
          setError('Server is busy. Please wait a moment and try again.');
        } else if (error.response?.status === 500) {
          setError(serverDetail || 'Server error. Please try logging in again.');
        } else if (!error.response) {
          setError('Unable to reach server. Please check your internet connection and try again.');
        } else {
          setError(serverDetail || `Authentication failed (status ${error.response?.status || 'unknown'}). Please try again.`);
        }
        
        setTimeout(() => navigate('/', { replace: true }), 4000);
      }
    };

    processSession();
  }, [navigate, location.hash]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center space-y-4 max-w-sm w-full">
        {(status === 'processing' || status === 'retrying') && (
          <>
            <div className="w-14 h-14 sm:w-16 sm:h-16 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-slate-600 font-medium text-sm sm:text-base">
              {status === 'retrying' ? `Retrying... (Attempt ${retryCount + 1}/4)` : 'Signing you in...'}
            </p>
            <p className="text-xs sm:text-sm text-slate-400">Please wait while we verify your credentials</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 font-medium">Login successful!</p>
            <p className="text-xs sm:text-sm text-slate-400">Redirecting to your dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 font-medium text-sm sm:text-base">Login Error</p>
            <p className="text-xs sm:text-sm text-slate-500">{error}</p>
            {/* "Try again" clears any stale localStorage / sessionStorage so a
                fresh OAuth round-trip can start cleanly — covers the
                "Invalid state parameter" mobile failure mode when the user's
                first attempt left orphaned state in storage. */}
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.removeItem('user');
                  sessionStorage.clear();
                } catch (_e) { /* storage may be locked in private mode */ }
                navigate('/', { replace: true });
              }}
              className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#E97832] hover:bg-[#C25E20] text-white text-sm font-medium transition-colors"
              data-testid="auth-error-try-again-btn"
            >
              Try again
            </button>
            <p className="text-xs text-slate-400">Or wait — we'll redirect you back to the login page automatically.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
